import { MINUTES_IN_HOUR } from '@stdlib/constants-time';

import { OpeningHoursType } from 'app/typings/core/admin/experiences/openingHours/openingHours';
import type {
  Opening,
  OpeningHours,
} from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';

export const JS_DAY_TO_DAY_OF_WEEK: Record<number, DayOfWeek> = {
  0: DAY_OF_WEEK.SUNDAY,
  1: DAY_OF_WEEK.MONDAY,
  2: DAY_OF_WEEK.TUESDAY,
  3: DAY_OF_WEEK.WEDNESDAY,
  4: DAY_OF_WEEK.THURSDAY,
  5: DAY_OF_WEEK.FRIDAY,
  6: DAY_OF_WEEK.SATURDAY,
};

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);

  return hours * MINUTES_IN_HOUR + (minutes || 0);
};

const getOpeningForDate = (
  openingHours: OpeningHours,
  date: Date
): Opening | null => {
  if (openingHours.type === OpeningHoursType.WEEKLY) {
    return openingHours;
  }

  return openingHours.days[JS_DAY_TO_DAY_OF_WEEK[date.getDay()]] ?? null;
};

const listOpenings = (openingHours: OpeningHours): Opening[] => {
  if (openingHours.type === OpeningHoursType.WEEKLY) {
    return [openingHours];
  }

  return Object.values(openingHours.days).filter(
    (opening): opening is Opening => Boolean(opening)
  );
};

export const isWithinOpeningHours = (
  openingHours: OpeningHours | undefined,
  date: Date
): boolean => {
  if (!openingHours) {
    return true;
  }

  const opening = getOpeningForDate(openingHours, date);

  if (!opening) {
    return false;
  }

  if (opening.isAllDay) {
    return true;
  }

  const minutes = date.getHours() * MINUTES_IN_HOUR + date.getMinutes();

  return opening.slots.some(
    (slot) => minutes >= toMinutes(slot.start) && minutes < toMinutes(slot.end)
  );
};

export const isDayClosedByOpeningHours = (
  openingHours: OpeningHours | undefined,
  date: Date
): boolean => {
  if (!openingHours) {
    return false;
  }

  const opening = getOpeningForDate(openingHours, date);

  if (!opening) {
    return true;
  }

  return !opening.isAllDay && opening.slots.length === 0;
};

export const getEarliestOpeningStartMinutes = (
  openingHours: OpeningHours | undefined
): number | null => {
  if (!openingHours) {
    return null;
  }

  const starts = listOpenings(openingHours).flatMap((opening) =>
    opening.isAllDay ? [] : opening.slots.map((slot) => toMinutes(slot.start))
  );

  return starts.length ? Math.min(...starts) : null;
};
